import React, { useState, useEffect } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsDetail from '../core/HeadersAndFooters/FooterButtonsDetail';
import ConfirmationModal from '../core/ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ShareLinkModal from '../core/ShareLinkModal';
import FederationMemberDetailModal from './FederationMemberDetailModal';
import GroupJoinLinkCard from './GroupJoinLinkCard';
import { isPlainEmail, sendOutcomeGroup } from '../../utils/invitationRules';
import StandardButton from '../core/StandardButton';
import GroupClosingBanner from './GroupClosingBanner';
import { useGroupClosing } from '../../hooks/useGroupClosing';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { groupService } from '../../data/dataService';
import { Share2, X, Info } from 'lucide-react';

// Import supabaseFetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';

const AdminGroup = ({ userId, onBack, currentContext, group, postCreate = false }) => {
  // Is the group closing (Roadmap #103)? Shuts the page below while it is.
  const closingState = useGroupClosing(group?.id || null, userId ? { id: userId } : null);
  const theme = useTheme();
  const { t, formattingLocale, locale } = useTranslation('groups');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [inviteEmails, setInviteEmails] = useState('');
  const [modal, setModal] = useState({ visible: false, message: '', onOk: null });
  const [showPostCreateBanner, setShowPostCreateBanner] = useState(postCreate);

  // #78: this form already knows what "changed" means — the pending member changes.
  const { confirmLeave, markSaved, dialog: leaveDialog } = useUnsavedChanges({
    dirty: Object.keys(pendingChanges).length > 0,
    onLeave: () => { if (onBack) onBack(); },
  });

  // Logo click listener - navigate back to MainFeed; asks first when something changed (#78).
  useEffect(() => {
    const handleLogoClick = async () => {
      if ((await confirmLeave()) && onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack, confirmLeave]);

  // Join requests state
  const [pendingRequests, setPendingRequests] = useState([]);
  const [requestResponses, setRequestResponses] = useState({});
  const [processingRequests, setProcessingRequests] = useState({});

  // Blocklist state
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [processingBlocks, setProcessingBlocks] = useState({});

  // Email invitations state
  const [inviteResults, setInviteResults] = useState([]);
  const [invitationCounts, setInvitationCounts] = useState({});
  const [copiedToken, setCopiedToken] = useState(null);
  const [revokingToken, setRevokingToken] = useState(null);
  const [sendingEmails, setSendingEmails] = useState(false);

  // Smart Link modal state
  const [showShareModal, setShowShareModal] = useState(false);

  // Federation members (owner/admin-only contact details, 4C-follow-on) state
  const [fedMembersAdmin, setFedMembersAdmin] = useState({ isFederation: false, members: [] });
  const [loadingFedMembersAdmin, setLoadingFedMembersAdmin] = useState(false);
  // Which member's detail modal is open (addendum-1: names-only list + on-demand modal)
  const [viewingFedMember, setViewingFedMember] = useState(null);

  // Page title - show group name in header
  const pageTitle = group?.name ? `${t('admin')} - ${group.name}` : t('admin');
  const headerContext = currentContext && typeof currentContext === 'object'
    ? { ...currentContext, name: pageTitle }
    : { name: pageTitle };

  // Load group members and pending requests
  useEffect(() => {
    if (group && group.id) {
      loadMembers();
      loadPendingRequests();
      loadInvitationCounts();
      loadBlockedUsers();
      loadFedMembersAdmin();
    }
  }, [group]);

  // Load federation members + their approved application answers. Always called (not gated on
  // group.is_federation from the prop, which isn't reliably complete on this page) — the
  // service call itself resolves is_federation fresh and returns isFederation:false when it
  // isn't one, so the section renders nothing rather than an empty heading.
  const loadFedMembersAdmin = async () => {
    setLoadingFedMembersAdmin(true);
    try {
      const result = await groupService.getFederationMembersWithApplications(group.id);
      setFedMembersAdmin(result || { isFederation: false, members: [] });
    } catch (err) {
      console.error('Error loading federation members:', err);
      setFedMembersAdmin({ isFederation: false, members: [] });
    } finally {
      setLoadingFedMembersAdmin(false);
    }
  };

  // Load invitation counts (sent/pending/accepted/expired/revoked) — always
  // available without ever listing the addresses behind them (R4).
  const loadInvitationCounts = async () => {
    try {
      const counts = await groupService.getInvitationCounts(group.id);
      setInvitationCounts(counts || {});
    } catch (err) {
      console.error('Error loading invitation counts:', err);
    }
  };

  // Load pending join requests
  const loadPendingRequests = async () => {
    try {
      const requests = await groupService.getPendingJoinRequests(group.id);
      setPendingRequests(requests || []);
    } catch (err) {
      console.error('Error loading pending requests:', err);
      setPendingRequests([]);
    }
  };

  // Load the group's blocklist
  const loadBlockedUsers = async () => {
    try {
      const blocks = await groupService.getBlockedUsers(group.id);
      setBlockedUsers(blocks || []);
    } catch (err) {
      console.error('Error loading blocked users:', err);
      setBlockedUsers([]);
    }
  };

  // Block a join requester — confirm first (Block resolves the pending request and enforces
  // the block at the DB; 20260906b_owner_block_person_from_group.sql).
  const handleBlockRequest = (request) => {
    const name = request.user?.display_name || t('unknown_user');
    setModal({
      visible: true,
      message: t('block_confirm_message', { name }),
      okLabel: t('block_action'),
      cancelLabel: t('common:cancel'),
      onOk: async () => {
        setModal({ visible: false, message: '', onOk: null });
        setProcessingRequests(prev => ({ ...prev, [request.id]: 'blocking' }));
        try {
          const success = await groupService.blockJoinRequester(group.id, request.user_id, userId);
          if (success) {
            setPendingRequests(prev => prev.filter(r => r.id !== request.id));
            await loadBlockedUsers();
          } else {
            setModal({
              visible: true,
              message: t('block_error'),
              onOk: () => setModal({ visible: false, message: '', onOk: null })
            });
          }
        } catch (err) {
          console.error('Error blocking user:', err);
          setModal({
            visible: true,
            message: t('block_error'),
            onOk: () => setModal({ visible: false, message: '', onOk: null })
          });
        } finally {
          setProcessingRequests(prev => {
            const newState = { ...prev };
            delete newState[request.id];
            return newState;
          });
        }
      },
      onCancel: () => setModal({ visible: false, message: '', onOk: null })
    });
  };

  // Unblock — after this the person may request to join again, with no lingering lockout.
  const handleUnblockUser = async (block) => {
    setProcessingBlocks(prev => ({ ...prev, [block.id]: true }));
    try {
      const success = await groupService.unblockJoinRequester(group.id, block.blocked_user_id);
      if (success) {
        setBlockedUsers(prev => prev.filter(b => b.id !== block.id));
      } else {
        setModal({
          visible: true,
          message: t('unblock_error'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    } catch (err) {
      console.error('Error unblocking user:', err);
    } finally {
      setProcessingBlocks(prev => {
        const newState = { ...prev };
        delete newState[block.id];
        return newState;
      });
    }
  };

  // Handle accept join request
  const handleAcceptRequest = async (requestId) => {
    const responseMessage = requestResponses[requestId] || null;
    setProcessingRequests(prev => ({ ...prev, [requestId]: 'accepting' }));

    try {
      const success = await groupService.acceptJoinRequest(requestId, userId, responseMessage);

      if (success) {
        // Remove from pending and reload members
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        setRequestResponses(prev => {
          const newResponses = { ...prev };
          delete newResponses[requestId];
          return newResponses;
        });
        await loadMembers();

        setModal({
          visible: true,
          message: t('member_accepted_success'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } else {
        throw new Error('Failed to accept request');
      }
    } catch (err) {
      console.error('Error accepting request:', err);
      setModal({
        visible: true,
        message: t('accept_request_error'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setProcessingRequests(prev => {
        const newState = { ...prev };
        delete newState[requestId];
        return newState;
      });
    }
  };

  // Handle reject join request
  const handleRejectRequest = async (requestId) => {
    const responseMessage = requestResponses[requestId] || null;
    setProcessingRequests(prev => ({ ...prev, [requestId]: 'rejecting' }));

    try {
      const success = await groupService.rejectJoinRequest(requestId, userId, responseMessage);

      if (success) {
        // Remove from pending list
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        setRequestResponses(prev => {
          const newResponses = { ...prev };
          delete newResponses[requestId];
          return newResponses;
        });

        setModal({
          visible: true,
          message: t('request_rejected'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      } else {
        throw new Error('Failed to reject request');
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      setModal({
        visible: true,
        message: t('reject_request_error'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setProcessingRequests(prev => {
        const newState = { ...prev };
        delete newState[requestId];
        return newState;
      });
    }
  };

  // Handle response message change
  const handleResponseChange = (requestId, message) => {
    setRequestResponses(prev => ({ ...prev, [requestId]: message }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(formattingLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const loadMembers = async () => {
    try {
      setLoading(true);

      // First get community members
      const { data: membersData, error: membersError } = await supabaseFetch.query(
        'community_members',
        {
          select: 'id, user_id, role, status, joined_at',
          eq: { community_id: group.id, role: 'member' },
          order: 'joined_at.desc'
        }
      );

      if (membersError) throw membersError;

      if (!membersData || membersData.length === 0) {
        setMembers([]);
        return;
      }

      // Get user details for all members — display name and avatar only. A
      // group owner has no legitimate read of a member's email address
      // (Architect ruling, 3 Aug 2026).
      const userIds = membersData.map(m => m.user_id);
      const { data: usersData, error: usersError } = await supabaseFetch.rpc(
        'get_public_profiles',
        { p_user_ids: userIds }
      );

      if (usersError) throw usersError;

      // Map users to members
      const usersMap = (usersData || []).reduce((acc, u) => {
        acc[u.id] = u;
        return acc;
      }, {});

      const membersWithUsers = membersData.map(m => ({
        ...m,
        user: usersMap[m.user_id] || null
      }));

      setMembers(membersWithUsers);
    } catch (err) {
      console.error('Error loading members:', err);
      setModal({
        visible: true,
        message: t('load_members_error'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (memberId, newStatus) => {
    // Track pending changes instead of saving immediately
    setPendingChanges(prev => ({
      ...prev,
      [memberId]: newStatus
    }));

    // Update local state to reflect the change in the UI
    setMembers(prevMembers =>
      prevMembers.map(member =>
        member.id === memberId ? { ...member, status: newStatus } : member
      )
    );
  };

  // A count line that reads correctly at 1 and at many (Build 2 §6): <key>_one / <key>_many.
  const countLine = (key, count, vars = {}) => t(`${key}_${count === 1 ? 'one' : 'many'}`, { count, ...vars });

  // Invitations Build 2 Phase 3 (#54). Each address gets a personal invitation
  // (invite_group_members: already_member / invited / already_invited / rate_limited /
  // invalid_address), then OurNet emails it (send-invitation, on this admin's own token). The
  // copyable link stays whatever happens to the email, and each address says whether it was
  // emailed and, if not, why (§3, §4). The address is an input only: the database keeps a hash,
  // so this list is the only place the addresses exist on OurNet (R1, R4).
  const handleInviteMembers = async () => {
    const entered = [...new Set(inviteEmails.split(/[,;\n]+/).map(e => e.trim()).filter(Boolean))];
    if (entered.length === 0) {
      setModal({
        visible: true,
        message: t('enter_email_error'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
      return;
    }

    // Only plain addresses are invited (addendum 2 item 3; the database's own rule). The others
    // are named in the summary rather than stopping the rest.
    const plain = entered.filter(isPlainEmail);
    const notPlain = entered.filter(e => !isPlainEmail(e));

    try {
      setLoading(true);
      let results = [];
      if (plain.length > 0) {
        const { success, results: rows } = await groupService.inviteMembers(group.id, plain);
        if (!success) throw new Error('invite_failed');
        results = rows || [];
      }
      const invalid = [...notPlain, ...results.filter(r => r.outcome === 'invalid_address').map(r => r.email)];

      // Email every invitation that has a link — new ones, and (Advisor S5) ones already pending,
      // so an email that failed last time is tried again; one already emailed costs nothing.
      const withLinks = results.filter(r => (r.outcome === 'invited' || r.outcome === 'already_invited') && r.token);
      setSendingEmails(true);
      const outcomes = withLinks.length > 0
        ? await groupService.sendInvitationEmails(withLinks.map(r => r.token), locale)
        : {};
      setSendingEmails(false);
      const shown = withLinks.map(r => ({ ...r, emailStatus: sendOutcomeGroup(outcomes[r.token]) }));

      const countOf = (outcome) => results.filter(r => r.outcome === outcome).length;
      const emailedNow = shown.filter(r => r.emailStatus === 'emailed').length;
      const notEmailed = shown.filter(r => !['emailed', 'already_emailed', 'in_progress'].includes(r.emailStatus)).length;
      const messageParts = [];
      if (countOf('invited') > 0) messageParts.push(countLine('invite_summary_created', countOf('invited')));
      if (countOf('already_invited') > 0) messageParts.push(countLine('invite_summary_already_invited', countOf('already_invited')));
      if (emailedNow > 0) messageParts.push(countLine('invite_summary_emailed', emailedNow));
      if (notEmailed > 0) messageParts.push(countLine('invite_summary_not_emailed', notEmailed));
      if (countOf('already_member') > 0) messageParts.push(countLine('invite_summary_already_member', countOf('already_member')));
      if (countOf('rate_limited') > 0) messageParts.push(countLine('invite_summary_rate_limited', countOf('rate_limited')));
      if (invalid.length > 0) messageParts.push(countLine('invite_summary_invalid', invalid.length, { emails: invalid.join(', ') }));

      setModal({
        visible: true,
        message: messageParts.join('\n') || t('invite_members_error'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });

      setInviteResults(shown);
      // Keep what was not invited in the box, so it can be corrected and sent again.
      setInviteEmails(invalid.join(', '));

      await loadMembers();
      await loadInvitationCounts();
    } catch (err) {
      console.error('Error inviting members:', err?.message);
      setModal({
        visible: true,
        message: t('invite_members_error'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setSendingEmails(false);
      setLoading(false);
    }
  };

  const handleCopyInviteLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(link);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      console.error('Error copying invite link:', err);
    }
  };

  const handleRevokeInvite = async (link) => {
    const token = link.split('/invite/')[1];
    if (!token) return;

    setRevokingToken(link);
    try {
      const revoked = await groupService.revokeInvitation(token);
      if (revoked) {
        setInviteResults(prev => prev.filter(r => r.link !== link));
        await loadInvitationCounts();
      } else {
        setModal({
          visible: true,
          message: t('revoke_invite_error'),
          onOk: () => setModal({ visible: false, message: '', onOk: null })
        });
      }
    } catch (err) {
      console.error('Error revoking invitation:', err);
      setModal({
        visible: true,
        message: t('revoke_invite_error'),
        onOk: () => setModal({ visible: false, message: '', onOk: null })
      });
    } finally {
      setRevokingToken(null);
    }
  };

  const handleSaveAndExit = async () => {
    try {
      setLoading(true);

      // Save all pending status changes
      if (Object.keys(pendingChanges).length > 0) {
        for (const [memberId, newStatus] of Object.entries(pendingChanges)) {
          const { error } = await supabaseFetch.update(
            'community_members',
            { status: newStatus },
            { eq: { id: memberId } }
          );

          if (error) throw error;
        }
      }

      // Show success message
      markSaved();
      setModal({
        visible: true,
        message: t('changes_saved_success'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
          if (onBack) {
            onBack();
          }
        }
      });
    } catch (err) {
      console.error('Error saving changes:', err);
      setModal({
        visible: true,
        message: t('save_changes_error'),
        onOk: () => {
          setModal({ visible: false, message: '', onOk: null });
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Cancel asks only when something changed — the shared pattern (#78).
  const handleCancel = async () => {
    if ((await confirmLeave()) && onBack) onBack();
  };

  // While the group is closing the admin page is shut (Roadmap #103, brief ruling 4): the notice,
  // with Undo for the owner, instead of the controls. Once undone, the page below renders as usual.
  if (closingState.closing) {
    return (
      <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
        <HeaderContainer />
        <HeaderButtonsForm currentContext={headerContext} onBack={onBack} headerTitle="" />
        <div style={{ paddingTop: '100px', paddingLeft: '40px', paddingRight: '40px' }}>
          <GroupClosingBanner
            closing
            closesOn={closingState.closesOn}
            variant="admin"
            canUndo={closingState.canUndo}
            undoing={closingState.undoing}
            undoFailed={closingState.undoFailed}
            onUndo={closingState.undo}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDF2F8] flex flex-col">
      {/* Header */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={headerContext}
        onBack={onBack}
        headerTitle=""
        onCheckUnsaved={confirmLeave}
      />

      {/* Post-creation info banner */}
      {showPostCreateBanner && (
        <div style={{
          position: 'relative',
          margin: '100px 40px 0 40px',
          padding: '14px 44px 14px 16px',
          background: '#EBF5FF',
          border: '1px solid #BFDBFE',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Info size={20} color="#2563EB" style={{ flexShrink: 0 }} />
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#1E40AF',
            lineHeight: '1.4'
          }}>
            {t('post_create_info')}
          </p>
          <button
            onClick={() => setShowPostCreateBanner(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              color: '#6B7280'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 ${showPostCreateBanner ? 'pt-[16px]' : 'pt-[100px]'} pb-[220px]`}>
        <div className="flex" style={{ position: 'relative', padding: '0 40px' }}>
          {/* Left Column - Invite More Members */}
          <div style={{
            width: '300px',
            minWidth: '300px',
            paddingRight: '24px'
          }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#BB4D9A' }}>
              {t('invite_members_title')}
            </h2>

            {/* Smart Link Sharing Button — not offered for a private group (Smart Links Phase 2,
                S5); the database also refuses the insert (S3/S4), this just avoids offering what
                would be refused. Description names what a click actually does — auto-join vs.
                request-to-join (S7) — the two states this card can still appear in. */}
            {!group.is_private && (
              <div className="bg-white rounded-lg p-4 shadow-md mb-4">
                <div className="flex flex-col gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1 text-sm">{t('smart_link_title')}</h3>
                    <p className="text-xs text-gray-500">
                      {t(group.auto_accept_members ? 'smart_link_description' : 'smart_link_description_approval')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity text-sm"
                    style={{ background: theme?.primary || '#BB4D9A' }}
                  >
                    <Share2 size={16} />
                    {t('create_link')}
                  </button>
                </div>
              </div>
            )}

            {/* Federation Members — owner/admin-only, names-only list with per-member detail
                modal (addendum-1, 13 Sep, superseding the original inline-answers build from
                earlier the same day — see docs/briefs/2026-09-13-federation-member-list-
                addendum-1-names-and-modal.md). Below the Invite Members dialogue, above Invite
                People (Michael's exact placement). Absent entirely when the group isn't a
                federation — not an empty section with a heading. */}
            {fedMembersAdmin.isFederation && (
              <div className="bg-white rounded-lg p-4 shadow-md mb-4">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('federation_members_admin_title')}</h3>
                {loadingFedMembersAdmin ? (
                  <p className="text-xs text-gray-500">{t('loading_federation_members')}</p>
                ) : fedMembersAdmin.members.length === 0 ? (
                  <p className="text-xs text-gray-500">{t('no_federation_members_yet')}</p>
                ) : (
                  <div className="space-y-2">
                    {/* The name IS the control (Michael's ruling, superseding the earlier
                        separate "View details" StandardButton design — that put two
                        widgets in one row and collapsed the name to zero width under
                        flex-shrink in this narrow column). One row, one native <button>:
                        nothing else competes for width, so the collapse can't recur.
                        Raw <button>, not StandardButton variant="inline", as a documented
                        exception (LAYOUT.md § Usage Rule) — the inline shape's render
                        supports exactly one Lucide icon + a text span, no slot for an
                        arbitrary logo <img> or a colour-filled initials circle; this is a
                        markup-support gap, not a colour one (inline now takes
                        inlineTextColor). A member with no application on file (e.g.
                        migrated from group_links, 4E) still appears here — the modal
                        itself says so. */}
                    {fedMembersAdmin.members.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => setViewingFedMember(member)}
                        className="w-full bg-gray-50 rounded p-3 border border-gray-200 flex items-center gap-2 text-left cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        {member.logo_url ? (
                          <img
                            src={member.logo_url}
                            alt=""
                            aria-hidden="true"
                            className="w-8 h-8 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div
                            aria-hidden="true"
                            className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                            style={{ background: theme?.primary || '#BB4D9A' }}
                          >
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                        <span className="font-semibold text-gray-900 text-sm truncate">
                          {member.name || t('unknown_group')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shared join link (Invitations Build 2 §5) */}
            <GroupJoinLinkCard groupId={group.id} />

            {/* Email Invitations */}
            <div className="bg-white rounded-lg p-4 shadow-md">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm">{t('add_users_title')}</h3>
              <label className="block text-xs text-gray-600 mb-2">
                {t('add_users_description')}
              </label>
              <textarea
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                placeholder={t('email_placeholder')}
                rows={3}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#BB4D9A] resize-none mb-3 text-sm"
              />
              <button
                onClick={handleInviteMembers}
                disabled={loading}
                className="w-full px-4 py-2 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
                style={{ background: theme?.primary || '#BB4D9A' }}
              >
                {sendingEmails ? t('invite_sending') : loading ? t('common:saving') : t('add_members')}
              </button>

              {/* Counts — always available, never a stored address list (R4) */}
              {invitationCounts.sent > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 text-xs text-gray-600">
                  {/* Keys by variable: invite_count_{sent,pending,accepted,expired,revoked}_{one,many} */}
                  {['sent', 'pending', 'accepted', 'expired', 'revoked']
                    .filter(status => status === 'sent' || invitationCounts[status] > 0)
                    .map(status => (
                      <span key={status}>{countLine(`invite_count_${status}`, invitationCounts[status])}</span>
                    ))}
                </div>
              )}

              {/* Invitation links — the only place these addresses exist on
                  OurNet. Each says whether OurNet emailed it, and the link is
                  always there to copy as well (Build 2 §3). The link itself is
                  shown, not just the address behind it (defect report item 4). */}
              {inviteResults.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">{t('invite_links_hint')}</p>
                  <div className="space-y-2">
                    {inviteResults.map((r) => (
                      <div key={r.link} className="bg-gray-50 rounded p-2">
                        <div className="text-xs text-gray-500 truncate">{r.email}</div>
                        {/* Keys by variable: invite_email_status_{emailed,opted_out,limit,already_emailed,in_progress,failed} */}
                        <div className={`text-xs mb-1 ${r.emailStatus === 'emailed' || r.emailStatus === 'already_emailed' ? 'text-gray-500' : 'text-gray-800 font-medium'}`}>
                          {t(`invite_email_status_${r.emailStatus}`)}
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="flex-1 text-xs text-gray-700 truncate"
                            title={r.link}
                          >
                            {r.link}
                          </span>
                          <StandardButton
                            variant="inline"
                            iconName={copiedToken === r.link ? 'Check' : 'Copy'}
                            label={t('copy_invite_link')}
                            onClick={() => handleCopyInviteLink(r.link)}
                            title={t('copy_invite_link')}
                          />
                          <StandardButton
                            variant="inline"
                            iconName="Ban"
                            onClick={() => handleRevokeInvite(r.link)}
                            disabled={revokingToken === r.link}
                            title={t('revoke_invite')}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Left Vertical Divider */}
          <div style={{
            position: 'absolute',
            left: '340px',
            top: 0,
            bottom: 0,
            width: '2px',
            background: '#e5e7eb'
          }} />

          {/* Center Column - Main Content */}
          <div style={{
            flex: 1,
            paddingLeft: '36px',
            paddingRight: '24px'
          }}>
            {/* Pending Join Requests Section - Compact.
                Heading: "Join" is person-side language a 12 Sep ruling keeps separate from
                federation. Driven by the list's actual composition, not group.is_federation —
                a federation-kind row can only exist when its target IS a federation (the
                INSERT policy's own is_federation=true check), so "every pending row is
                federation-kind" already implies the group is one; checking the rows directly
                needs no read of group.is_federation at all, complete-object or otherwise, and
                stays correct if a membership-kind row (the DB doesn't forbid one, only the UI
                no longer offers it) ever lands in the same list — the heading falls back to
                the generic wording rather than mislabeling a mixed list as all-applications. */}
            {pendingRequests.length > 0 && (
              <div className="flex justify-center mb-4">
                <div className="w-full max-w-4xl">
                  <div className="bg-white rounded-lg p-4 shadow-md border-l-4" style={{ borderLeftColor: '#F59E0B' }}>
                    <h2 className="text-lg font-bold mb-3" style={{ color: '#F59E0B' }}>
                      {pendingRequests.every(r => r.request_kind === 'federation')
                        ? t('pending_federation_applications_title', { count: pendingRequests.length })
                        : t('pending_join_requests_title', { count: pendingRequests.length })}
                    </h2>

                    <div className="space-y-2">
                      {pendingRequests.map((request) => {
                        const isFederationRequest = request.request_kind === 'federation';
                        // The Federator's current schema, matched by stable key — not the
                        // schema as it stood when the applicant answered. An answer whose key
                        // no longer exists is shown under its raw key, never dropped (4C §2).
                        const schemaByKey = (request.federation_application_schema || []).reduce((acc, field) => {
                          acc[field.key] = field;
                          return acc;
                        }, {});
                        const answerEntries = isFederationRequest
                          ? Object.entries(request.request_data || {})
                          : [];

                        return (
                          <div
                            key={request.id}
                            className="bg-gray-50 rounded p-3 border border-gray-200"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              {/* Subject: a federation request is about the APPLICANT GROUP —
                                  its identity is the subject, the human who filed it is
                                  secondary (4C §2). A membership request is about the person. */}
                              {isFederationRequest ? (
                                <div className="flex items-center gap-2">
                                  {request.applicant_group?.logo_url ? (
                                    <img
                                      src={request.applicant_group.logo_url}
                                      alt={request.applicant_group.name}
                                      className="w-8 h-8 rounded object-cover"
                                    />
                                  ) : (
                                    <div
                                      className="w-8 h-8 rounded flex items-center justify-center text-white font-bold text-xs"
                                      style={{ background: theme?.primary || '#BB4D9A' }}
                                    >
                                      {request.applicant_group?.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-semibold text-gray-900 text-sm">
                                      {request.applicant_group?.name || t('unknown_group')}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {t('federation_request_badge')}
                                      {' · '}
                                      {t('federation_request_filed_by', { name: request.user?.display_name || t('unknown_user') })}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {request.user?.avatar_url ? (
                                    <img
                                      src={request.user.avatar_url}
                                      alt={request.user.display_name}
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div
                                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                      style={{ background: theme?.primary || '#BB4D9A' }}
                                    >
                                      {request.user?.display_name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-semibold text-gray-900 text-sm">
                                      {request.user?.display_name || t('unknown_user')}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Action Buttons — Accept/Reject route through the same branched
                                  accept_join_request/reject_join_request for both kinds. Block
                                  is never offered on a federation request: the block store is
                                  keyed to a person, and blocking here would block whoever
                                  happened to press submit, not what the Federator means (4C §2). */}
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAcceptRequest(request.id)}
                                  disabled={!!processingRequests[request.id]}
                                  className="px-3 py-1 bg-green-500 text-white font-semibold rounded text-xs hover:bg-green-600 transition-colors disabled:opacity-50"
                                >
                                  {processingRequests[request.id] === 'accepting' ? '...' : t('common:accept')}
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(request.id)}
                                  disabled={!!processingRequests[request.id]}
                                  className="px-3 py-1 bg-red-500 text-white font-semibold rounded text-xs hover:bg-red-600 transition-colors disabled:opacity-50"
                                >
                                  {processingRequests[request.id] === 'rejecting' ? '...' : t('common:reject')}
                                </button>
                                {!isFederationRequest && (
                                  <StandardButton
                                    variant="inline"
                                    iconName="Ban"
                                    label={processingRequests[request.id] === 'blocking' ? '...' : t('block_action')}
                                    onClick={() => handleBlockRequest(request)}
                                    disabled={!!processingRequests[request.id]}
                                    title={t('block_action')}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Request Message (membership only) */}
                            {!isFederationRequest && request.request_message && (
                              <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                                <div className="text-xs text-gray-700 italic">
                                  "{request.request_message}"
                                </div>
                              </div>
                            )}

                            {/* Federation application answers — labeled from the federation's
                                CURRENT schema, matched by key. A key with no current schema
                                entry (the Federator reworded/removed the question since this
                                was filed) is shown under its raw key, never dropped. */}
                            {isFederationRequest && answerEntries.length > 0 && (
                              <div className="mt-2 p-2 bg-white rounded border border-gray-200 space-y-1">
                                {answerEntries.map(([key, value]) => (
                                  <div key={key} className="text-xs text-gray-700">
                                    <span className="font-semibold">{schemaByKey[key]?.label || key}:</span>{' '}
                                    {String(value)}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Response Message Input */}
                            <div className="mt-2">
                              <input
                                type="text"
                                value={requestResponses[request.id] || ''}
                                onChange={(e) => handleResponseChange(request.id, e.target.value)}
                                placeholder={t('optional_response_placeholder')}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#BB4D9A]"
                                disabled={!!processingRequests[request.id]}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Blocklist - minimal, Block/Unblock only, no bulk tools, no search */}
            {blockedUsers.length > 0 && (
              <div className="flex justify-center mb-4">
                <div className="w-full max-w-4xl">
                  <div className="bg-white rounded-lg p-4 shadow-md border-l-4" style={{ borderLeftColor: '#6b7280' }}>
                    <h2 className="text-lg font-bold mb-3" style={{ color: '#6b7280' }}>
                      {t('blocklist_title', { count: blockedUsers.length })}
                    </h2>

                    <div className="space-y-2">
                      {blockedUsers.map((block) => (
                        <div
                          key={block.id}
                          className="bg-gray-50 rounded p-3 border border-gray-200 flex items-center justify-between gap-3"
                        >
                          <div className="flex items-center gap-2">
                            {block.user?.avatar_url ? (
                              <img
                                src={block.user.avatar_url}
                                alt={block.user.display_name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                style={{ background: '#6b7280' }}
                              >
                                {block.user?.display_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                            <div className="font-semibold text-gray-900 text-sm">
                              {block.user?.display_name || t('unknown_user')}
                            </div>
                          </div>

                          <StandardButton
                            variant="inline"
                            iconName="Unlock"
                            label={processingBlocks[block.id] ? '...' : t('unblock_action')}
                            onClick={() => handleUnblockUser(block)}
                            disabled={!!processingBlocks[block.id]}
                            title={t('unblock_action')}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Members Section - Compact */}
            <div className="flex justify-center">
              <div className="w-full max-w-4xl">
                <div className="bg-white rounded-lg p-4 shadow-md">
                  <h2 className="text-lg font-bold mb-3" style={{ color: '#BB4D9A' }}>{t('members_title')}</h2>

                  {loading && members.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">{t('loading_members')}</div>
                  ) : members.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">{t('no_members_yet')}</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 font-semibold text-sm" style={{ color: '#BB4D9A' }}>
                              {t('name_column')}
                            </th>
                            <th className="text-center py-2 px-3 font-semibold text-sm" style={{ color: '#BB4D9A' }}>
                              {t('status_column')}
                            </th>
                            <th className="text-center py-2 px-3 font-semibold text-sm" style={{ color: '#BB4D9A' }}>
                              {t('action_column')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member) => (
                            <tr key={member.id} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="py-2 px-3 text-sm">
                                {member.user?.display_name || t('unknown_user')}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <div
                                  className="inline-block w-16 h-6 rounded-full text-xs font-semibold text-white flex items-center justify-center"
                                  style={{
                                    backgroundColor: member.status === 'joined' ? '#5DBEA3' : '#EF4444'
                                  }}
                                >
                                  {member.status === 'joined' ? '✓' : '?'}
                                </div>
                              </td>
                              <td className="py-2 px-3 text-center">
                                <select
                                  value={member.status}
                                  onChange={(e) => handleStatusChange(member.id, e.target.value)}
                                  className="px-3 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:border-[#BB4D9A]"
                                  style={{ minWidth: '100px' }}
                                >
                                  <option value="requested">{t('status_requested')}</option>
                                  <option value="joined">{t('status_joined')}</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons moved to footer */}
          </div>

          {/* Right Vertical Divider */}
          <div style={{
            position: 'absolute',
            right: '340px',
            top: 0,
            bottom: 0,
            width: '2px',
            background: '#e5e7eb'
          }} />

          {/* Right Column - Reserved Space */}
          <div style={{
            width: '300px',
            minWidth: '300px',
            paddingLeft: '24px'
          }}>
            {/* Reserved for future functionality */}
          </div>
        </div>
      </div>

      {/* Footer */}
      <FooterContainer viewType="form">
        <FooterButtonsDetail
          currentContext={currentContext}
          viewType="form"
          onCancel={handleCancel}
          onSave={handleSaveAndExit}
          loading={loading}
          saveLabel={loading ? t('common:saving') : t('save_and_exit')}
          cancelLabel={t('common:cancel')}
        />
      </FooterContainer>

      {/* Confirmation Modal */}
      {leaveDialog}

      <ConfirmationModal
        visible={modal.visible}
        title={modal.title || t('common:confirmation')}
        message={modal.message}
        okLabel={modal.okLabel}
        cancelLabel={modal.cancelLabel}
        onOk={() => {
          try { modal.onOk && modal.onOk(); } catch (e) { console.error(e); setModal({ visible: false, message: '', onOk: null }); }
        }}
        onCancel={() => {
          try {
            if (modal.onCancel) modal.onCancel();
            else setModal({ visible: false, message: '', onOk: null });
          } catch (e) {
            console.error(e);
            setModal({ visible: false, message: '', onOk: null });
          }
        }}
      />

      {/* Share Link Modal */}
      <ShareLinkModal
        visible={showShareModal}
        targetType="community"
        targetId={group?.id}
        targetName={group?.name}
        currentUser={{ id: userId }}
        onClose={() => setShowShareModal(false)}
      />

      {/* Federation Member Detail Modal (addendum-1) */}
      <FederationMemberDetailModal
        isOpen={!!viewingFedMember}
        onClose={() => setViewingFedMember(null)}
        member={viewingFedMember}
      />
    </div>
  );
};

export default AdminGroup;
